import React, { useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated, Easing } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useAppStore } from '../../store/appStore';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export type DateRangePreset = 'today' | '7d' | '30d' | '90d';

const PRESETS: { id: DateRangePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
];

export interface DashboardToolbarProps {
  dateRange: DateRangePreset;
  onDateRangeChange: (preset: DateRangePreset) => void;
  onRefresh: () => void;
  refreshing?: boolean;
}

/** Dashboard header/toolbar — business context, date-range presets, refresh.
 * No calendar picker: presets are all this admin dashboard needs, and no
 * DateRangePicker component exists anywhere in the codebase to reuse. */
export const DashboardToolbar: React.FC<DashboardToolbarProps> = ({
  dateRange,
  onDateRangeChange,
  onRefresh,
  refreshing = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const { currentBusiness } = useAppStore();
  const spinAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (refreshing) {
      spinAnim.setValue(0);
      Animated.loop(
        Animated.timing(spinAnim, { toValue: 1, duration: 700, easing: Easing.linear, useNativeDriver: true })
      ).start();
    } else {
      spinAnim.stopAnimation();
      spinAnim.setValue(0);
    }
  }, [refreshing, spinAnim]);

  const spin = spinAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });

  return (
    <View style={styles.container}>
      <View style={styles.left}>
        <Text style={styles.businessName} numberOfLines={1}>{currentBusiness?.name ?? 'Dashboard'}</Text>
        <Text style={styles.subtitle}>Overview</Text>
      </View>

      <View style={styles.right}>
        <View style={styles.pillGroup}>
          {PRESETS.map((p) => (
            <TouchableOpacity
              key={p.id}
              style={[styles.pill, dateRange === p.id && styles.pillActive]}
              onPress={() => onDateRangeChange(p.id)}
              activeOpacity={0.8}
            >
              <Text style={[styles.pillText, dateRange === p.id && styles.pillTextActive]}>{p.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <TouchableOpacity
          style={styles.refreshButton}
          onPress={onRefresh}
          disabled={refreshing}
          activeOpacity={0.75}
        >
          <Animated.View style={{ transform: [{ rotate: spin }] }}>
            <Ionicons name="refresh" size={18} color={colors.text.primary as string} />
          </Animated.View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  left: {
    gap: 2,
  },
  businessName: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  subtitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  right: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  pillGroup: {
    flexDirection: 'row',
    gap: 2,
    backgroundColor: colors.background.base,
    borderRadius: designTokens.borderRadius.full,
    padding: 2,
    borderWidth: 1,
    borderColor: colors.border,
  },
  pill: {
    paddingHorizontal: designTokens.spacing.sm,
    paddingVertical: 6,
    borderRadius: designTokens.borderRadius.full,
  },
  pillActive: {
    backgroundColor: colors.brand.primary,
  },
  pillText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
  },
  pillTextActive: {
    color: colors.text.onPrimary,
  },
  refreshButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.base,
  },
});
